import { useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { PortfolioProductOverviewEntry, PortfolioProductPricing } from '@mews-commander/data-access';
import { ApplicationState } from '@mews-commander/types';
import { getTaxationByTaxRateCode } from '@mews-commander/legacy-entities/src/ducks/taxEnvironments';
import { EnrichedTaxValues, mapEntryWithEnrichedTaxes } from '../utils/mapEntryWithEnrichedTaxes';

export const useProductsWithTaxation = (data: PortfolioProductOverviewEntry[]) => {
    const uniqueTaxRateCodes = [
        ...new Set(
            data.flatMap(({ pricing }) =>
                pricing.discriminator === PortfolioProductPricing.Absolute
                    ? pricing.value.taxValues.map(({ taxRateCode }) => taxRateCode).filter((code) => code != null)
                    : pricing.value.taxRateCodes
            )
        ),
    ];

    const taxationMapping = useSelector((state: ApplicationState) =>
        getTaxationByTaxRateCode(state, { taxRateCodes: uniqueTaxRateCodes })
    );

    const getEnrichedTaxData = useCallback(
        (entry: PortfolioProductOverviewEntry) => {
            const { discriminator, value } = entry.pricing;

            if (discriminator === PortfolioProductPricing.Absolute) {
                return value.taxValues
                    .filter((taxValue) => taxValue.taxRateCode)
                    .map((taxValue) => ({
                        ...taxValue,
                        ...(taxValue.taxRateCode ? taxationMapping[taxValue.taxRateCode] : {}),
                    }));
            } else if (discriminator === PortfolioProductPricing.Relative) {
                return value.taxRateCodes.map((code) => ({
                    taxRateCode: code,
                    ...taxationMapping[code],
                }));
            }
            return null;
        },
        [taxationMapping]
    );

    const mappedData = useMemo(
        () =>
            data?.map((entry) => {
                const enrichedTaxData = getEnrichedTaxData(entry);
                return enrichedTaxData
                    ? mapEntryWithEnrichedTaxes(entry, enrichedTaxData as EnrichedTaxValues[])
                    : entry;
            }),
        [data, getEnrichedTaxData]
    );

    return { mappedData };
};
